import { type FC, useState } from 'react';

import CustomTable from 'components/table/CustomTable';
import { SubTitle } from 'components/text/SubTitle';
import { Modal, Tooltip, Typography, formatDate, openModal } from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { FunctionalTestMethod } from 'modules/dashboard/core/enum';

import { ReactComponent as ViewNoteIcon } from '../../../assets/icons/note-32.svg';

import type { AssessmentResult } from '../core/type';
import type { HeaderTableItem } from 'components/table/type';

import { convertToTimeString } from 'helpers';
import { sortBy, sumBy } from 'lodash';

import { MAX_ADDITIONAL_SCORE } from '../../../constants';
import { LearnerResultStatusCard } from './LearnerResultStatusCard';
import { MixedChart } from './MixedChart';

export const AssessmentsComp: FC<{ assessmentResult: AssessmentResult[] }> = ({
  assessmentResult,
}) => {
  const [comment, setComment] = useState<string>('');
  const [isHidden, setIsHidden] = useState<[boolean, boolean]>([true, false]);

  const header: HeaderTableItem[] = [
    {
      title: '',
      field: 'collapse',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
    },
    {
      title: 'Assessment',
      field: 'assessment',
      style: {
        width: '65%',
        minWidth: 'calc(65% - 550px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Status',
      field: 'status',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Completed on',
      field: 'isCompleted',
      style: {
        width: '15%',
        minWidth: 200,
      },
    },
    {
      title: '',
      field: 'viewNote',
      style: {
        width: 60,
        minWidth: 20,
      },
    },
  ];

  const headersChildren: HeaderTableItem[] = [
    {
      title: '#',
      field: 'index',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        textAlign: 'center',
      },
    },
    {
      title: 'Functional test',
      field: 'functionalTest',
      style: {
        width: '65%',
        minWidth: 'calc(65% - 550px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Metric',
      field: 'metric',
      style: {
        width: '25%',
        minWidth: 350,
      },
    },
    {
      title: '',
      field: 'viewNote',
      style: {
        width: 60,
        minWidth: 20,
      },
    },
  ];

  const renderBody = (index: number, row: any, column: HeaderTableItem) => {
    switch (column.field) {
      case 'assessment':
        return (
          <div>
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              {row.assessment.name}
            </Typography>
            {row.assessment.isClass && row.assessment.isOnboarding ? (
              <SubTitle leftTitle="Onboarding" rightTitle="Class" />
            ) : (
              <Typography style={{ fontSize: '16px', lineHeight: '22px', color: '#808080' }}>
                {row.assessment.isClass ? 'Class assessment' : 'Baseline fitness test'}
              </Typography>
            )}
          </div>
        );

      case 'score':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.assessment.assessmentScore}/{row.assessment.maxScore}
          </Typography>
        );

      case 'status':
        return <LearnerResultStatusCard status={row.assessment.status} />;

      case 'isCompleted':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.assessment.completedOn
              ? formatDate(row.assessment.completedOn, 'iii, d LLL, hh:mm a ')
              : '-'}
          </Typography>
        );
    }
  };

  const renderBodyChildren = (index: number, row: any, column: HeaderTableItem) => {
    const maxScore = row.functionalTest.maxScore;
    const totalMaxScore = row.functionalTest.isAddition
      ? maxScore + MAX_ADDITIONAL_SCORE
      : maxScore;

    const score =
      Number(row.detail?.score) > Number(row.functionalTest.maxScore)
        ? Number(row.functionalTest.maxScore)
        : Number(row.detail?.score);
    const additionalScore =
      row.functionalTest.isAddition && row.detail?.scoreAddition ? row.detail?.scoreAddition : 0;
    const totalScore = score + Math.min(additionalScore, MAX_ADDITIONAL_SCORE);

    switch (column.field) {
      case 'index':
        return <Typography>{index + 1}</Typography>;

      case 'functionalTest':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={row.functionalTest.thumbnail}
              style={{ width: 48, height: 48, borderRadius: 12 }}
            />
            <div>
              <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                {row.functionalTest.name}
              </Typography>
              {row.functionalTest.type ? (
                <SubTitle leftTitle={'Score entry'} rightTitle={row.functionalTest.type} />
              ) : (
                <Typography
                  style={{
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontWeight: 400,
                    color: '#808080',
                  }}
                >
                  {row.functionalTest.method?.toLowerCase() === FunctionalTestMethod.Kg
                    ? 'Weight entry'
                    : row.functionalTest.method?.toLowerCase() === FunctionalTestMethod.Time
                      ? 'Time entry'
                      : 'Rep count entry'}
                </Typography>
              )}
            </div>
          </div>
        );

      case 'score':
        return (
          <>
            {row.functionalTest.method.toLowerCase() !== FunctionalTestMethod.Kg &&
              row.functionalTest.method.toLowerCase() !== FunctionalTestMethod.Time && (
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  {row?.detail ? `${totalScore}/${totalMaxScore}` : 'No score'}
                </Typography>
              )}
          </>
        );

      case 'metric':
        if (!row?.detail || row.functionalTest.type) {
          return <></>;
        }

        if (row.functionalTest.method.toLowerCase() === FunctionalTestMethod.Kg) {
          return (
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              Weight: {row.detail.score ?? 0} kg
            </Typography>
          );
        }

        if (row.functionalTest.method.toLowerCase() === FunctionalTestMethod.Time) {
          return (
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              Time: {convertToTimeString(Number(row.detail.timeCount ?? 0), true)}
            </Typography>
          );
        }

        return (
          <>
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              Reps: {row.detail.score ?? 0} <br />
              Time: {convertToTimeString(row.detail.timeCount, true)} <br />
            </Typography>
            {/* {row.functionalTest.isSO2AndHR && (
              <div>
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  Pre-test:{' '}
                  {!row.detail.preHR && !row.detail.preSpO2 ? (
                    <span style={{ color: '#808080' }}>No data</span>
                  ) : (
                    `${row.detail.preSpO2 ? `SpO2 = ${row.detail.preSpO2}%,` : ''} ${row.detail.preHR ? `HR = ${row.detail.preHR} bpm` : ''}`
                  )}
                </Typography>
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  Post-test:{' '}
                  {!row.detail.postHR && !row.detail.postSpO2 ? (
                    <span style={{ color: '#808080' }}>No data</span>
                  ) : (
                    `${row.detail.postSpO2 ? `SpO2 = ${row.detail.postSpO2}%,` : ''} ${row.detail.postHR ? `HR = ${row.detail.postHR} bpm` : ''}`
                  )}
                </Typography>
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  Recovery:{' '}
                  {!row.detail.recoveryHR && !row.detail.recoverySpO2 ? (
                    <span style={{ color: '#808080' }}>No data</span>
                  ) : (
                    `${row.detail.recoverySpO2 ? `SpO2 = ${row.detail.recoverySpO2}%,` : ''} ${row.detail.recoveryHR ? `HR = ${row.detail.recoveryHR} bpm` : ''}`
                  )}
                </Typography>
              </div>
            )} */}
          </>
        );

      case 'viewNote':
        return row?.detail?.comment ? (
          <>
            <Tooltip placement="top" arrow={false} content="View note" offset={[0, -4]}>
              <ViewNoteIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setComment(row.detail.comment);
                  openModal(MODAL_KEY.VIEW_NOTE);
                }}
              />
            </Tooltip>
          </>
        ) : null;
    }
  };

  const newAssessmentResult = sortBy(assessmentResult, 'assessment.completedOn');

  return (
    <div>
      <div className="border-container p-16">
        <MixedChart
          setIsHidden={setIsHidden}
          isHidden={isHidden}
          data={{
            labels: newAssessmentResult.map((el) =>
              formatDate(String(el.assessment.completedOn), 'd MMM'),
            ),
            datasets: [
              {
                type: 'line',
                label: '% of max score',
                borderColor: '#49A3F1',
                data: newAssessmentResult.map(
                  (el) =>
                    (sumBy(
                      el.items.map((it: any) => ({
                        ...it,
                        score:
                          Number(it?.detail?.score) > Number(it?.detail?.functionalTest?.maxScore)
                            ? Number(it?.detail?.functionalTest?.maxScore)
                            : Number(it?.detail?.score),
                      })),
                      'score',
                    ) /
                      sumBy(el.items, 'detail.functionalTest.maxScore')) *
                    100,
                ),
                backgroundColor: '#49A3F1',
                yAxisID: 'y',
                hidden: isHidden[0],
              },
              {
                type: 'bar',
                label: 'Score',
                data: newAssessmentResult.map((el) => Number(el.assessment.assessmentScore)),
                barPercentage: 0.2,
                borderRadius: 10,
                yAxisID: 'y1',
                hidden: isHidden[1],
              },
            ],
          }}
        />
      </div>

      <div className="border-container p-16 mt-16">
        <CustomTable
          headers={header}
          bodies={newAssessmentResult.map((el, index) => ({ ...el, id: index }))}
          renderBody={renderBody}
          headersChildren={headersChildren}
          renderBodyChildren={renderBodyChildren}
        />
      </div>

      <Modal
        header={
          <Typography variant="h4" style={{ fontSize: 22, fontWeight: '500' }}>
            Note
          </Typography>
        }
        closeIconStyle={{
          width: 24,
          height: 24,
        }}
        modalKey={MODAL_KEY.VIEW_NOTE}
        submitButton={{ hide: true }}
        cancelButton={{ hide: true }}
        onCancel={() => {
          setComment('');
        }}
      >
        <Typography variant="body-2" style={{ fontSize: 18 }}>
          {comment}
        </Typography>
      </Modal>
    </div>
  );
};
